import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clampFloatingMenuPosition } from '../utils/floatingMenuPosition';

/**
 * Tiny Link | Embed tab that appears after pasting a YouTube / Vimeo / Loom
 * URL into a normal doc block (Notion-style). Default is already a link —
 * "Embed" converts it to a video block.
 */
export const VideoPasteChoice = ({ position, onLink, onEmbed, onClose }) => {
  const menuRef = useRef(null);
  const [adjusted, setAdjusted] = useState(position);

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return undefined;
    const apply = () => setAdjusted(clampFloatingMenuPosition(menu, position));
    apply();
    const raf = requestAnimationFrame(apply);
    window.addEventListener('resize', apply);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', apply);
    };
  }, [position]);

  useEffect(() => {
    const onScroll = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [onClose]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [onClose]);

  return createPortal(
    <div
      ref={menuRef}
      className="doc-video-paste-choice"
      style={{ top: adjusted.y, left: adjusted.x }}
      role="toolbar"
      aria-label="Paste as link or embed"
    >
      <button
        type="button"
        className="doc-video-paste-choice__btn doc-video-paste-choice__btn--active"
        onClick={(e) => {
          e.stopPropagation();
          onLink();
        }}
      >
        Link
      </button>
      <button
        type="button"
        className="doc-video-paste-choice__btn"
        onClick={(e) => {
          e.stopPropagation();
          onEmbed();
        }}
      >
        Embed
      </button>
    </div>,
    document.body
  );
};
